import { Centrifuge } from "centrifuge";
import type { PublicationContext } from "centrifuge";
import { apiClient } from "./api";

// Default to the QuantForge API host so feature-branch / preview deploys work
// with no per-app env config; override via VITE_QF_* only for a non-prod backend.
// (Mirrors the VITE_API_BASE_URL fallback convention in api.ts.)
// Must stay a grey-cloud host. Do not use https://fznanalytics.com or
// https://app.fzntrading.com — those are orange and 1010/403 urllib/curl.
const QF_API_BASE =
  (import.meta.env.VITE_QF_API_BASE as string) || "https://api.fznanalytics.com";
const QF_WS_URL =
  (import.meta.env.VITE_QF_WS_URL as string) ||
  "wss://api.fznanalytics.com/connection/websocket";

// QF signs the Centrifugo connect token and the REST bearer with *different*
// HMAC keys, so they are two separate tokens from two endpoints. QF's Centrifugo
// validates with a single secret and has no dual-accept -- sending the bearer on
// the WebSocket connect path breaks the connection. Keep these two apart.

// Centrifugo connect token (carries the ticker scope).
let _tokenFetcher = async (): Promise<string> => {
  const r = await apiClient.get<{ token: string }>("/api/option-stream/qf/connection-token/");
  return r.token;
};

// REST bearer token (same claims, dedicated signing key).
let _bearerFetcher = async (): Promise<string> => {
  const r = await apiClient.get<{ token: string }>("/api/option-stream/qf/rest-bearer-token/");
  return r.token;
};

// Portal mints a 3600s JWT. Reminting on every REST call stampeded
// /rest-bearer-token/ (slow-request warnings) and raced SESSION_SAVE.
// Cache until 60s before exp; single-flight concurrent mints.
const BEARER_REFRESH_SKEW_MS = 60_000;
const BEARER_UNKNOWN_EXP_MS = 5 * 60_000;

let _cachedBearer: { token: string; expiresAtMs: number } | null = null;
let _bearerInFlight: Promise<string> | null = null;

// Bumped every time the cached bearer is genuinely invalidated. A request that
// 401s reports the generation it *used*; if that is already stale, some sibling
// has re-minted since and this request must not clear the newer token.
//
// ⚠️ **Without this, concurrent 401s stampede.** A cold page load fires ~7 QF
// queries at once. Each one clearing the cache on 401 also wiped
// `_bearerInFlight` — the promise a sibling was already awaiting — so they
// orphaned each other's mints and the losers were left with no valid token.
// That is the 2026-08-20 outage: symbol-summary, company-profile,
// contract-leaders, top-contracts, meta and ohlc all going red together and
// intermittently, on a page whose bearer was perfectly healthy.
let _bearerGeneration = 0;

function jwtExpMs(token: string): number | null {
  const parts = token.split(".");
  if (parts.length < 2 || !parts[1]) return null;
  try {
    const pad = parts[1].length % 4 === 0 ? "" : "=".repeat(4 - (parts[1].length % 4));
    const json = atob(parts[1].replace(/-/g, "+").replace(/_/g, "/") + pad);
    const payload = JSON.parse(json) as { exp?: unknown };
    return typeof payload.exp === "number" ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

function clearBearerCache(): void {
  _cachedBearer = null;
  _bearerInFlight = null;
  _bearerGeneration += 1;
}

/**
 * Invalidate the bearer only if the caller's token is still the current one.
 *
 * Returns true when this call performed the invalidation. A request holding a
 * superseded generation is a straggler — a sibling has already re-minted, so it
 * should retry with the new token rather than throwing the new token away.
 */
function invalidateBearer(generation: number): boolean {
  if (generation !== _bearerGeneration) return false;
  clearBearerCache();
  return true;
}

/** The bearer, plus the generation it belongs to. */
async function getRestBearer(): Promise<{ token: string; generation: number }> {
  const now = Date.now();
  if (_cachedBearer && _cachedBearer.expiresAtMs - BEARER_REFRESH_SKEW_MS > now) {
    return { token: _cachedBearer.token, generation: _bearerGeneration };
  }
  if (_bearerInFlight) {
    // ⚠️ Read the generation AFTER awaiting. Capturing it first would label the
    // token with the generation that was current when we started waiting.
    const token = await _bearerInFlight;
    return { token, generation: _bearerGeneration };
  }
  const generation = _bearerGeneration;
  _bearerInFlight = (async () => {
    try {
      const token = await _bearerFetcher();
      const exp = jwtExpMs(token);
      _cachedBearer = {
        token,
        expiresAtMs: exp ?? now + BEARER_UNKNOWN_EXP_MS,
      };
      return token;
    } finally {
      // ⚠️ Only retire our own in-flight promise. If an invalidation replaced
      // it while we were minting, clearing it here would orphan a sibling.
      if (generation === _bearerGeneration) _bearerInFlight = null;
    }
  })();
  const token = await _bearerInFlight;
  return { token, generation };
}

// Test seam: replace the Centrifugo connect-token fetcher in unit tests.
export function __setTokenFetcher(fn: () => Promise<string>): void {
  _tokenFetcher = fn;
}

// Test seam: replace the REST bearer fetcher in unit tests.
export function __setBearerFetcher(fn: () => Promise<string>): void {
  _bearerFetcher = fn;
  clearBearerCache();
}

async function qfAuthorizedFetch(
  url: string,
  init: RequestInit,
  path: string,
  verb: "REST" | "PATCH",
): Promise<Response> {
  const { token, generation } = await getRestBearer();
  let res = await fetch(url, {
    ...init,
    headers: { ...init.headers, Authorization: `Bearer ${token}` },
  });
  if (res.status === 401) {
    // ⚠️ Generation-gated. Clearing unconditionally is what let concurrent
    // 401s wipe each other's freshly-minted tokens; a straggler simply picks
    // up whatever the winner minted.
    invalidateBearer(generation);
    const fresh = await getRestBearer();
    res = await fetch(url, {
      ...init,
      headers: { ...init.headers, Authorization: `Bearer ${fresh.token}` },
    });
  }
  if (!res.ok) {
    throw await qfError(res, verb, path);
  }
  return res;
}

/**
 * A QF failure that carries the server's JSON body.
 *
 * Shaped as `{ response: { status, data } }` so consumers written against
 * axios errors keep working — `GexRayWidget`'s `tickerNotInPlanFrom()` reads
 * `err.response.data` and cannot see a plain `Error`.
 *
 * The body matters: QF refuses an out-of-plan ticker with a structured
 * `ticker_not_in_plan` payload that drives the upgrade card. Discarding it
 * (which this client used to do) made a 403 indistinguishable from any other
 * failure, so a Zeroday user asking for NVDA got the generic pre-market
 * message instead of an upgrade prompt.
 */
export interface QfHttpError extends Error {
  response: { status: number; data: unknown };
}

async function qfError(
  res: Response,
  verb: string,
  path: string,
): Promise<QfHttpError> {
  let data: unknown;
  try {
    data = await res.clone().json();
  } catch {
    // Non-JSON body (HTML error page, empty 502). Leave `data` undefined —
    // consumers already guard on its shape.
  }
  const err = new Error(`QF ${verb} ${path} -> ${res.status}`) as QfHttpError;
  err.response = { status: res.status, data };
  return err;
}

export async function qfRestGet<T = unknown>(
  path: string,
  params?: Record<string, string>,
): Promise<T> {
  const qs = params ? new URLSearchParams(params).toString() : "";
  const url = qs ? `${QF_API_BASE}${path}?${qs}` : `${QF_API_BASE}${path}`;
  const res = await qfAuthorizedFetch(url, {}, path, "REST");
  return (await res.json()) as T;
}

export async function qfRestPatch<T = unknown>(
  path: string,
  body: unknown,
): Promise<T> {
  // MUST be the REST bearer, never the Centrifugo connect token -- QF signs the
  // two with different HMAC keys and its Centrifugo has no dual-accept.
  const res = await qfAuthorizedFetch(
    `${QF_API_BASE}${path}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
    path,
    "PATCH",
  );
  return (await res.json()) as T;
}

async function subscriptionToken(channel: string): Promise<string> {
  const r = await apiClient.get<{ token: string }>(
    `/api/option-stream/qf/subscription-token/?channel=${encodeURIComponent(channel)}`,
  );
  return r.token;
}

let _client: Centrifuge | null = null;

export function qfCentrifuge(): Centrifuge {
  if (_client) return _client;
  _client = new Centrifuge(QF_WS_URL, {
    getToken: async () => _tokenFetcher(),
  });
  _client.connect();
  return _client;
}

// Run `cb` each time the QF Centrifugo transport (re)connects. Fires on the
// initial connect and on every reconnect — callers use this to re-seed REST
// history after a gap, then resume live WS updates. Returns an unsubscribe fn.
export function onQfConnected(cb: () => void): () => void {
  const c = qfCentrifuge();
  c.on('connected', cb);
  return () => c.off('connected', cb);
}

/**
 * Subscribe to QF transport up/down transitions.
 *
 * `onQfConnected` only fires on connect, so a widget using it alone can never
 * tell that the feed has since dropped. Anything rendering a "live" indicator
 * for QF-sourced data must use this instead of portal's `isConnected` — the two
 * transports fail independently, and portal being up says nothing about whether
 * QF alerts are still arriving.
 *
 * @param cb Called with the new state on every connect/disconnect.
 * @returns Unsubscribe function.
 */
export function onQfConnectionChange(cb: (connected: boolean) => void): () => void {
  const c = qfCentrifuge();
  const onUp = (): void => cb(true);
  const onDown = (): void => cb(false);
  c.on('connected', onUp);
  c.on('disconnected', onDown);
  c.on('error', onDown);
  return () => {
    c.off('connected', onUp);
    c.off('disconnected', onDown);
    c.off('error', onDown);
  };
}

/** Current QF transport state — seeds UI before the first transition fires. */
export function isQfConnected(): boolean {
  return _client?.state === 'connected';
}

const _subRefCounts = new Map<string, number>();

export function qfSubscribe(
  channel: string,
  onPublication: (data: unknown) => void,
): () => void {
  const c = qfCentrifuge();
  const sub =
    c.getSubscription(channel) ??
    c.newSubscription(channel, {
      getToken: async () => subscriptionToken(channel),
    });
  const handler = (ctx: PublicationContext) => onPublication(ctx.data);
  sub.on("publication", handler);
  _subRefCounts.set(channel, (_subRefCounts.get(channel) ?? 0) + 1);
  // centrifuge no-ops re-subscribing an already-subscribed sub.
  sub.subscribe();
  return () => {
    sub.off("publication", handler);
    const next = (_subRefCounts.get(channel) ?? 1) - 1;
    if (next <= 0) {
      _subRefCounts.delete(channel);
      sub.unsubscribe();
    } else {
      _subRefCounts.set(channel, next);
    }
  };
}
